import { useState, useMemo, useEffect } from 'react';
import { downloadBlob } from '../utils/fileHelpers';
import { exportTaxXlsx } from '../utils/taxExport';
import { useMergedExpenses } from '../hooks/useMergedExpenses';
import { getNextTierThatSupportsExportFormat} from '../hooks/useTier';
import PdfExportModal from './PdfExportModal';
import { HelpTip } from './HelpTip';
import { TIPS } from '../help/content';
import { trackExport } from '../analytics';
import { normalizeCategory, categoryLabel } from '../utils/categoryHelpers';

// ── Date-range helpers ────────────────────────────────────────────────────────

function toISO(d) {
  return d.toISOString().slice(0, 10);
}

function getPresetRange(preset) {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth();

  const monday = (d) => {
    const day = new Date(d);
    const dow = day.getDay();
    const diff = dow === 0 ? -6 : 1 - dow;
    day.setDate(day.getDate() + diff);
    return day;
  };

  switch (preset) {
    case 'last_year': { const ly = y - 1; return { start: `${ly}-01-01`, end: `${ly}-12-31` }; }
    case 'ytd':       return { start: `${y}-01-01`, end: toISO(today) };
    case 'last_month': { const f = new Date(y, m - 1, 1); const l = new Date(y, m, 0); return { start: toISO(f), end: toISO(l) }; }
    case 'mtd':       return { start: toISO(new Date(y, m, 1)), end: toISO(today) };
    case 'last_week': { const tm = monday(today); const lm = new Date(tm); lm.setDate(lm.getDate() - 7); const ls = new Date(lm); ls.setDate(ls.getDate() + 6); return { start: toISO(lm), end: toISO(ls) }; }
    case 'wtd':       return { start: toISO(monday(today)), end: toISO(today) };
    case 'today':     return { start: toISO(today), end: toISO(today) };
    default:          return { start: '', end: '' };
  }
}

const PRESETS = [
  { value: 'all',        label: 'All time' },
  { value: 'last_year',  label: 'Last Year' },
  { value: 'ytd',        label: 'Year-to-date' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'mtd',        label: 'Month-to-date' },
  { value: 'last_week',  label: 'Last Week' },
  { value: 'wtd',        label: 'Week-to-date' },
  { value: 'today',      label: 'Today' },
  { value: 'custom',     label: 'Custom range…' },
];

function drKey(uid) { return uid ? `expenser_export_daterange_${uid}` : 'expenser_export_daterange'; }

function loadPersistedRange(uid) {
  try {
    const saved = JSON.parse(localStorage.getItem(drKey(uid)) || 'null');
    if (saved?.preset) {
      if (saved.preset !== 'all' && saved.preset !== 'custom') {
        const { start, end } = getPresetRange(saved.preset);
        return { preset: saved.preset, startDate: start, endDate: end };
      }
      return saved;
    }
  } catch { /* ignore */ }
  return { preset: 'all', startDate: '', endDate: '' };
}

// ── Export row helpers ────────────────────────────────────────────────────────

function fmtAuditDate(iso) {
  if (!iso) return "";
  return iso.slice(0, 16).replace('T', ' ');
}

function buildRows(expenses) {
  return expenses.map(e => {
    const hasConversion = e.convertedTotal != null && e.convertedCurrency;
    const showRate = hasConversion && e.convertedCurrency !== e.currency;
    return {
      date:           e.date || "",
      vendor:         e.vendor || "",
      category:       categoryLabel(e.category) || e.category || "",
      origCurrency:   e.currency || "USD",
      subtotal:       parseFloat(e.subtotal) || 0,
      tax:            parseFloat(e.tax)      || 0,
      tip:            parseFloat(e.tip)      || 0,
      origTotal:      parseFloat(e.total)    || 0,
      reportCurrency: hasConversion ? e.convertedCurrency : (e.currency || "USD"),
      reportTotal:    hasConversion ? (parseFloat(e.convertedTotal) || 0) : (parseFloat(e.total) || 0),
      exchangeRate:   showRate ? (parseFloat(e.conversionRate) || 1) * (1 + (parseFloat(e.adjustmentFactor) || 0) / 100) : 1,
      conversionDate: e.conversionDate ? e.conversionDate.slice(0, 10) : "",
      notes:          e.notes || "",
      miles:          normalizeCategory(e.category) === 'mileage' ? (parseFloat(e.miles) || "") : "",
      mileageRate:    normalizeCategory(e.category) === 'mileage' ? (parseFloat(e.mileageRate) || "") : "",
      sourceFile:     e._sourceFile || "",
      createdBy:      e.createdBy || "",
      createdAt:      fmtAuditDate(e.createdAt),
      lastModifiedBy: e.lastModifiedBy || "",
      lastModifiedAt: fmtAuditDate(e.lastModifiedAt),
    };
  });
}

function getHeaders(isCombined, isShared) {
  const cols = [
    "Date", "Vendor", "Category",
    "Orig. Currency", "Subtotal", "Tax", "Tip", "Orig. Total",
    "Report Currency", "Report Total", "Exchange Rate", "Conversion Date", "Notes",
    "Miles", "Rate/mi"
  ];
  if (isCombined) cols.push("Source Report");
  if (isShared)   cols.push("Created By", "Created At", "Modified By", "Modified At");
  return cols;
}

function rowToArray(r, isCombined, isShared) {
  const base = [
    r.date, r.vendor, r.category,
    r.origCurrency, r.subtotal, r.tax, r.tip, r.origTotal,
    r.reportCurrency, r.reportTotal, r.exchangeRate, r.conversionDate, r.notes,
    r.miles, r.mileageRate,
  ];
  if (isCombined) base.push(r.sourceFile);
  if (isShared)   base.push(r.createdBy, r.createdAt, r.lastModifiedBy, r.lastModifiedAt);
  return base;
}

// Human-readable date range label for export headers
function formatDateRange(startDate, endDate, presetLabel) {
  if (!startDate && !endDate) return "All time";
  if (startDate && endDate) return `${startDate} to ${endDate}`;
  if (startDate) return `From ${startDate}`;
  return `Through ${endDate}`;
}

// ── Combine Files panel ───────────────────────────────────────────────────────

function CombineFilesPanel({ otherFiles, selectedNames, onToggle, loadState, currentFileName }) {
  return (
    <div style={{
      background: "#F9FAFB", border: "1px solid #E5E7EB",
      borderRadius: 12, padding: "14px 16px", marginBottom: 16,
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
        Combine Reports
      </div>
      <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 10 }}>
        Include expenses from other saved reports in this export.
      </div>

      {/* Active file — always included, non-interactive */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid #E5E7EB", marginBottom: 6 }}>
        <div style={{
          width: 16, height: 16, borderRadius: 4, flexShrink: 0,
          background: "#0A7C8A", display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <span style={{ color: "#fff", fontSize: 10, fontWeight: 800 }}>✓</span>
        </div>
        <span style={{ fontSize: 13, color: "#6B7280", fontStyle: "italic", flex: 1 }}>
          {currentFileName || "Current report"} <span style={{ fontSize: 11 }}>(active)</span>
        </span>
      </div>

      {otherFiles.map(f => {
        const checked  = selectedNames.has(f.name);
        const state    = loadState[f.name];
        const loading  = state === 'loading';
        const errored  = state === 'error';
        const count    = (f.data || []).length;

        return (
          <div
            key={f.name}
            onClick={() => !loading && onToggle(f.name)}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "8px 0", cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.6 : 1,
            }}
          >
            <div style={{
              width: 16, height: 16, borderRadius: 4, border: `2px solid ${checked ? '#0A7C8A' : '#D1D5DB'}`,
              background: checked ? "#0A7C8A" : "#fff", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "background 0.15s, border-color 0.15s",
            }}>
              {checked && <span style={{ color: "#fff", fontSize: 10, fontWeight: 800 }}>✓</span>}
            </div>
            <span style={{ fontSize: 13, color: "#374151", flex: 1 }}>{f.name}</span>
            <span style={{ fontSize: 11, color: "#9CA3AF" }}>
              {loading ? "Loading…" : errored ? "⚠ Error" : `${count} exp.`}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

function ExportModal({ expenses, defaultCurrency, onClose, getTaxCategory,
                       user, savedFiles, currentFileName, isShared,
                       tierLimits, tier, onUpgrade, tierConfig }) {
  const [isExporting, setIsExporting] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  // console.log("Tier limits: ", tierLimits);
  // console.log("tier:", tier);
  // Tier gates
  const canCombine      = tierLimits ? (tierLimits.canCombineReports ?? false) : false;
  const baseFormats     = tierLimits?.exportFormats ?? ['csv', 'xlsx', 'json', 'doc', 'taxreport'];
  // Guests (no account) can always export JSON so they can back up their local data
  const allowedFormats  = !user ? [...new Set([...baseFormats, 'json'])] : baseFormats;
  // console.log("canCombine:", canCombine);
  // console.log("allowedFormats:", allowedFormats);
  // Multi-file merge
  const {
    otherFiles, selectedNames, toggleFile, loadState,
    mergedExpenses, isLoadingAny,
  } = useMergedExpenses(
    user?.uid ?? null,
    expenses,
    savedFiles ?? [],
    currentFileName ?? null,
  );

  // Date range
  const uid = user?.uid ?? null;
  const [{ preset, startDate, endDate }, setRange] = useState(() => loadPersistedRange(uid));

  const saveRange = (next) => { setRange(next); localStorage.setItem(drKey(uid), JSON.stringify(next)); };

  const handlePresetChange = (value) => {
    if (value === 'custom') { saveRange({ preset: 'custom', startDate, endDate }); return; }
    const { start, end } = getPresetRange(value);
    saveRange({ preset: value, startDate: start, endDate: end });
  };

  const handleDateChange = (field, value) => {
    saveRange({
      preset: 'custom',
      startDate: field === 'startDate' ? value : startDate,
      endDate:   field === 'endDate'   ? value : endDate,
    });
  };

  // Apply date filter to merged expenses
  const filteredExpenses = useMemo(() => {
    if (!startDate && !endDate) return mergedExpenses;
    return mergedExpenses.filter(e => {
      if (!e.date) return false;
      if (startDate && e.date < startDate) return false;
      if (endDate   && e.date > endDate)   return false;
      return true;
    });
  }, [mergedExpenses, startDate, endDate]);

  const reportCurrency = defaultCurrency || "USD";
  const totalConverted = filteredExpenses.reduce(
    (sum, e) => sum + (parseFloat(e.convertedTotal ?? e.total) || 0), 0
  );

  const reportCount    = 1 + selectedNames.size;
  const isCombined     = reportCount > 1;
  const isDateFiltered = filteredExpenses.length !== mergedExpenses.length;
  const presetLabel    = PRESETS.find(p => p.value === preset)?.label || "Custom range";
  const dateRangeLabel = formatDateRange(startDate, endDate, presetLabel);
  const canExport      = !isExporting && !isLoadingAny && filteredExpenses.length > 0;

  // Build a smart default filename from the active report name + date range
  const defaultFilename = useMemo(() => {
    const base = currentFileName || "expense-report";
    if (!startDate && !endDate) return base;
    const suffix = startDate && endDate && startDate === endDate
      ? startDate
      : [startDate, endDate].filter(Boolean).join("_");
    return `${base}_${suffix}`;
  }, [currentFileName, startDate, endDate]);

  const [filename, setFilename] = useState(defaultFilename);

  // Sync default into the field whenever date range or active file changes
  useEffect(() => {
    setFilename(defaultFilename);
  }, [defaultFilename]);

  // ── CSV ────────────────────────────────────────────────────────────────────
  const exportCSV = () => {
    trackExport('csv');
    setIsExporting(true);
    try {
      const headers = getHeaders(isCombined, isShared);
      const rows = buildRows(filteredExpenses).map(r =>
        rowToArray(r, isCombined, isShared).map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")
      );
      // Metadata rows at the top (prefixed with #)
      const meta = [
        `"# Expense Report"`,
        `"# Date Range: ${dateRangeLabel}"`,
        `"# Generated: ${toISO(new Date())}"`,
        ...(isCombined ? [`"# Reports combined: ${reportCount}"`] : []),
        `""`,
      ];
      downloadBlob(
        new Blob([[...meta, headers.map(h => `"${h}"`).join(","), ...rows].join("\n")], { type: "text/csv" }),
        `${filename}.csv`
      );
      onClose();
    } finally { setIsExporting(false); }
  };

  // ── XLSX ───────────────────────────────────────────────────────────────────
  const exportXLSX = async () => {
    trackExport('xlsx');
    setIsExporting(true);
    try {
      const doExport = () => {
        const XLSX = window.XLSX;
        const rows = buildRows(filteredExpenses);
        const headers = getHeaders(isCombined, isShared);
        const colWidths = [10,22,18,8,9,9,9,10,8,10,12,12,20];
        if (isCombined) colWidths.push(22);
        if (isShared)   colWidths.push(22, 16, 22, 16);

        const ws = XLSX.utils.aoa_to_sheet([headers, ...rows.map(r => rowToArray(r, isCombined, isShared))]);
        ws['!cols'] = colWidths.map(w => ({ wch: w }));

        const byCat = {};
        rows.forEach(r => { byCat[r.category] = (byCat[r.category] || 0) + r.reportTotal; });
        const ws2 = XLSX.utils.aoa_to_sheet([
          ["Expense Report Summary"], [],
          ["Date Range",      dateRangeLabel],
          ["Generated",       toISO(new Date())],
          ["Report Currency", reportCurrency],
          ["Total Expenses",  filteredExpenses.length],
          ["Total Amount",    totalConverted],
          ...(isCombined ? [["Reports combined", reportCount]] : []),
          [],
          ["Category", `Amount (${reportCurrency})`],
          ...Object.entries(byCat).sort((a, b) => b[1] - a[1]).map(([cat, t]) => [cat, t]),
        ]);
        ws2['!cols'] = [{ wch: 24 }, { wch: 22 }];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws2, "Summary");
        XLSX.utils.book_append_sheet(wb, ws,  "Expenses");
        XLSX.writeFile(wb, `${filename}.xlsx`);
        onClose();
      };

      if (window.XLSX) { doExport(); return; }
      const s = document.createElement("script");
      s.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
      s.onload = doExport;
      document.head.appendChild(s);
    } finally { setIsExporting(false); }
  };

  // ── Word ───────────────────────────────────────────────────────────────────
  // (defined after exportXLSX so doExport closure is in scope; kept separate)
  const exportDOC = () => {
    trackExport('doc');
    setIsExporting(true);
    try {
      const rows = buildRows(filteredExpenses);
      const byCat = {};
      rows.forEach(r => { byCat[r.category] = (byCat[r.category] || 0) + r.reportTotal; });

      const summaryRows = Object.entries(byCat).sort((a, b) => b[1] - a[1])
        .map(([cat, t]) => `<tr><td>${cat}</td><td style="text-align:right">${reportCurrency} ${t.toFixed(2)}</td></tr>`)
        .join("");

      const detailRows = rows.map(r => `<tr>
        <td>${r.date}</td><td>${r.vendor}</td><td>${r.category}</td>
        <td style="text-align:right">${r.origCurrency} ${r.origTotal.toFixed(2)}</td>
        <td style="text-align:right">${r.reportCurrency} ${r.reportTotal.toFixed(2)}</td>
        <td style="text-align:right">${r.exchangeRate !== 1 ? r.exchangeRate.toFixed(6) : "—"}</td>
        <td>${r.conversionDate}</td><td>${r.notes}</td>
        ${isCombined ? `<td>${r.sourceFile}</td>` : ""}
        ${isShared ? `<td>${r.createdBy}</td><td>${r.createdAt}</td><td>${r.lastModifiedBy}</td><td>${r.lastModifiedAt}</td>` : ""}
      </tr>`).join("");

      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Expense Report</title>
<style>body{font-family:Arial,sans-serif;font-size:11pt}h1{font-size:16pt}h2{font-size:13pt;margin-top:18pt}
p.meta{font-size:10pt;color:#6B7280;margin:2pt 0}
table{border-collapse:collapse;width:100%;margin-bottom:18pt}
th{background:#0A7C8A;color:#fff;padding:5pt 7pt;text-align:left;font-size:10pt}
td{padding:4pt 7pt;border-bottom:1px solid #E5E7EB;font-size:10pt}
tr:nth-child(even) td{background:#F9FAFB}</style></head><body>
<h1>Expense Report${isCombined ? ` (${reportCount} reports combined)` : ""}</h1>
<p class="meta">Date range: <strong>${dateRangeLabel}</strong></p>
<p class="meta">Generated: ${toISO(new Date())}</p>
<p class="meta">Report currency: ${reportCurrency} &nbsp;·&nbsp; ${filteredExpenses.length} expenses &nbsp;·&nbsp; Total: ${reportCurrency} ${totalConverted.toFixed(2)}</p>
<h2>Summary</h2>
<table><tr><th>Category</th><th>Amount (${reportCurrency})</th></tr>
${summaryRows}
<tr style="font-weight:bold"><td>Total</td><td style="text-align:right">${reportCurrency} ${totalConverted.toFixed(2)}</td></tr>
</table>
<h2>Expense Detail</h2>
<table><tr><th>Date</th><th>Vendor</th><th>Category</th><th>Original Amount</th>
<th>Report Amount</th><th>Exchange Rate</th><th>Conv. Date</th><th>Notes</th>
${isCombined ? "<th>Source Report</th>" : ""}
${isShared ? "<th>Created By</th><th>Created At</th><th>Modified By</th><th>Modified At</th>" : ""}</tr>
${detailRows}</table></body></html>`;

      downloadBlob(
        new Blob(["\ufeff" + html], { type: "application/msword;charset=utf-8" }),
        `${filename}.doc`
      );
      onClose();
    } finally { setIsExporting(false); }
  };

  // ── JSON ──────────────────────────────────────────────────────────────────
  const exportJSON = () => {
    trackExport('json');
    setIsExporting(true);
    try {
      const clean = filteredExpenses.map(({ _sourceFile, ...e }) => e);
      downloadBlob(
        new Blob([JSON.stringify(clean, null, 2)], { type: "application/json" }),
        `${filename}.json`
      );
      onClose();
    } finally { setIsExporting(false); }
  };

  // ── Tax Report ─────────────────────────────────────────────────────────────
  const exportTaxReport = async () => {
    trackExport('tax_report');
    setIsExporting(true);
    try {
      await exportTaxXlsx(filteredExpenses, {
        reportCurrency,
        filename,
        getTaxCategory,
        dateRange: dateRangeLabel,
        isCombined,
        isShared,
      });
      onClose();
    } finally {
      setIsExporting(false);
    }
  };

  const exportOptions = [
    { name: "CSV",        format: "csv",       description: "Comma-separated values, compatible with Excel",    icon: "📊", action: exportCSV,                    color: "#10B981" },
    { name: "Excel",      format: "xlsx",      description: "XLSX format with summary sheet",                   icon: "📗", action: exportXLSX,                   color: "#059669" },
    { name: "PDF",        format: "pdf",       description: "Formatted report with optional receipt images",    icon: "📄", action: () => { trackExport('pdf'); setShowPdfModal(true); },  color: "#EF4444" },
    { name: "Word",       format: "doc",       description: "Document format with tables",                      icon: "📝", action: exportDOC,                    color: "#7C3AED" },
    { name: "Tax Report", format: "taxreport", description: "XLSX grouped by IRS deduction category",           icon: "🧾", action: exportTaxReport,              color: "#DC2626" },
    { name: "JSON",       format: "json",      description: "Full expense data — can be loaded back into app",  icon: "📦", action: exportJSON,                   color: "#F59E0B" },
  ];

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "flex-end", justifyContent: "center", zIndex: 1000, backdropFilter: "blur(4px)" }}
      onClick={onClose}
    >
      <div
        style={{ background: "#fff", borderRadius: "20px 20px 0 0", padding: "20px", width: "100%", maxWidth: 390, maxHeight: "90vh", overflowY: "auto", animation: "slideUp 0.3s ease-out" }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#111", lineHeight: 1.2 }}>Export Report</div>
            <div style={{ fontSize: 12, color: "#6B7280", marginTop: 1 }}>
              <strong style={{ color: isDateFiltered ? "#0A7C8A" : "#374151" }}>{filteredExpenses.length}</strong>
              {isDateFiltered ? ` of ${mergedExpenses.length}` : ""} expense{filteredExpenses.length !== 1 ? "s" : ""}
              {reportCount > 1 && <span style={{ color: "#0A7C8A" }}> · {reportCount} reports</span>}
              {" · "}<strong>{reportCurrency} {totalConverted.toFixed(2)}</strong>
              {isLoadingAny && <span style={{ color: "#0A7C8A" }}> · Loading…</span>}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#888", padding: 0, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>×</button>
        </div>

        {/* Compact date range */}
        <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
          <select
            value={preset}
            onChange={e => handlePresetChange(e.target.value)}
            style={{ flex: "1 1 120px", padding: "6px 8px", borderRadius: 7, border: "1.5px solid #E5E7EB", fontSize: 12, fontWeight: 600, color: "#374151", background: "#fff", outline: "none" }}
          >
            {PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          {[['startDate', startDate], ['endDate', endDate]].map(([field, val]) => (
            <input key={field} type="date" value={val} onChange={e => handleDateChange(field, e.target.value)}
              style={{ flex: "1 1 110px", padding: "6px 8px", borderRadius: 7, border: "1.5px solid #E5E7EB", fontSize: 12, color: "#374151", background: "#fff", outline: "none", colorScheme: "light" }} />
          ))}
        </div>

        {/* Combine files panel — premium only, requires 2+ saved files */}
        {canCombine && otherFiles.length > 0 && (
          <CombineFilesPanel
            otherFiles={otherFiles}
            selectedNames={selectedNames}
            onToggle={toggleFile}
            loadState={loadState}
            currentFileName={currentFileName}
          />
        )}

        {/* Filename */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
            Filename
          </div>
          <div style={{ display: "flex", alignItems: "center", border: "1.5px solid #E5E7EB", borderRadius: 8, overflow: "hidden", background: "#fff" }}>
            <input
              type="text"
              value={filename}
              onChange={e => setFilename(e.target.value.replace(/[/\\?%*:|"<>]/g, ""))}
              style={{ flex: 1, padding: "9px 10px", border: "none", outline: "none", fontSize: 13, color: "#374151", background: "transparent", fontFamily: "inherit" }}
              spellCheck={false}
            />
            <span style={{ padding: "9px 10px", fontSize: 12, color: "#9CA3AF", borderLeft: "1px solid #E5E7EB", background: "#F9FAFB", whiteSpace: "nowrap" }}>
              .csv / .xlsx / .pdf / .doc / .json
            </span>
          </div>
        </div>

        {/* Export buttons */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5 }}>Format</div>
          <HelpTip content={TIPS.exportFormats} placement="top" />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {exportOptions.map(opt => {
            const locked = !allowedFormats.includes(opt.format);
            const active = !locked && canExport;
            return (
              <button
                key={opt.name}
                onClick={() => {
                  if (locked) {
                    onUpgrade?.({
                      allowed: false,
                      featureName: `${opt.name} export`,
                      message: `${opt.name} export is not available on your current plan.`,
                      // upgradeTo: 'pro',
                      upgradeTo: getNextTierThatSupportsExportFormat(tier, opt.format, tierConfig),
                      upgradeMessage: `Upgrade to unlock ${opt.name} and other export formats.`,
                    });
                    return;
                  }
                  opt.action();
                }}
                disabled={!active && !locked}
                style={{
                  display: "flex", alignItems: "center", gap: 16,
                  padding: "14px 16px", borderRadius: 12,
                  border: "1.5px solid #E5E7EB", background: "#fff",
                  cursor: active || locked ? "pointer" : "not-allowed",
                  opacity: !locked && !canExport ? 0.5 : 1,
                  position: "relative",
                }}
                onMouseEnter={e => { if (active) { e.currentTarget.style.borderColor = opt.color; e.currentTarget.style.boxShadow = `0 4px 12px ${opt.color}20`; }}}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "#E5E7EB"; e.currentTarget.style.boxShadow = "none"; }}
              >
                <div style={{ width: 36, height: 36, borderRadius: 9, background: locked ? "#F3F4F6" : `${opt.color}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0, filter: locked ? "grayscale(1)" : "none" }}>
                  {opt.icon}
                </div>
                <div style={{ flex: 1, textAlign: "left" }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: locked ? "#9CA3AF" : "#111", marginBottom: 1 }}>{opt.name}</div>
                  <div style={{ fontSize: 12, color: "#9CA3AF" }}>{locked ? "Upgrade to unlock" : opt.description}</div>
                </div>
                <div style={{ fontSize: 16, color: locked ? "#D1D5DB" : "#9CA3AF" }}>{locked ? "🔒" : "→"}</div>
              </button>
            );
          })}
        </div>

        <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #E5E7EB" }}>
          <button onClick={onClose} disabled={isExporting}
            style={{ width: "100%", padding: "14px", borderRadius: 12, border: "1.5px solid #E5E7EB", background: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", color: "#6B7280" }}>
            {isExporting ? "Exporting…" : "Cancel"}
          </button>
        </div>
      </div>

      {showPdfModal && (
        <PdfExportModal
          expenses={filteredExpenses}
          reportName={filename || currentFileName || 'Expense Report'}
          dateRange={dateRangeLabel !== 'All time' ? dateRangeLabel : null}
          defaultCurrency={reportCurrency}
          onClose={() => { setShowPdfModal(false); onClose(); }}
        />
      )}
    </div>
  );
}

export default ExportModal;
